import { useState } from "react";
import { useServiceOrders, useUpdateServiceOrder } from "@/hooks/useServiceOrders";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Mail, Phone, Eye, CheckCircle2, Clock, XCircle } from "lucide-react";
import { format } from "date-fns";
import { hy } from "date-fns/locale";

export const ServiceOrdersSection = () => {
  const { data: orders, isLoading } = useServiceOrders();
  const updateOrder = useUpdateServiceOrder();
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
      pending: { label: "Նոր", variant: "default" },
      contacted: { label: "Կապ հաստատված", variant: "secondary" },
      in_progress: { label: "Ընթացքի մեջ", variant: "outline" },
      completed: { label: "Ավարտված", variant: "secondary" },
      cancelled: { label: "Չեղարկված", variant: "destructive" },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    await updateOrder.mutateAsync({ id: orderId, status: newStatus });
  };

  const viewDetails = (order: any) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ծառայությունների պատվերներ</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ծառայությունների պատվերներ</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <p className="text-muted-foreground">Պատվերներ չկան</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Ծառայությունների պատվերներ ({orders.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ամսաթիվ</TableHead>
                <TableHead>Հաճախորդ</TableHead>
                <TableHead>Ծառայություն</TableHead>
                <TableHead>Կապ</TableHead>
                <TableHead>Բյուջե</TableHead>
                <TableHead>Կարգավիճակ</TableHead>
                <TableHead>Գործողություններ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell className="whitespace-nowrap">
                    {format(new Date(order.created_at), "dd MMM yyyy, HH:mm", { locale: hy })}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{order.first_name} {order.last_name}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{order.service_name}</div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(`tel:${order.phone}`)}
                      >
                        <Phone className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(`mailto:${order.email}`)}
                      >
                        <Mail className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm text-muted-foreground">
                      {order.budget_range || "Նշված չէ"}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={order.status}
                      onValueChange={(value) => handleStatusChange(order.id, value)}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Նոր</SelectItem>
                        <SelectItem value="contacted">Կապ հաստատված</SelectItem>
                        <SelectItem value="in_progress">Ընթացքի մեջ</SelectItem>
                        <SelectItem value="completed">Ավարտված</SelectItem>
                        <SelectItem value="cancelled">Չեղարկված</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => viewDetails(order)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Դիտել
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Պատվերի մանրամասներ</DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h3 className="font-semibold mb-2">Հաճախորդի տվյալներ</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-muted-foreground">Անուն:</span> {selectedOrder.first_name} {selectedOrder.last_name}</p>
                    <p><span className="text-muted-foreground">Հեռախոս:</span> {selectedOrder.phone}</p>
                    <p><span className="text-muted-foreground">Էլ․փոստ:</span> {selectedOrder.email}</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Պատվերի տվյալներ</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-muted-foreground">Ծառայություն:</span> {selectedOrder.service_name}</p>
                    <p><span className="text-muted-foreground">Բյուջե:</span> {selectedOrder.budget_range || "Նշված չէ"}</p>
                    <p><span className="text-muted-foreground">Ժամկետ:</span> {selectedOrder.timeline || "Նշված չէ"}</p>
                    <p><span className="text-muted-foreground">Ամսաթիվ:</span> {format(new Date(selectedOrder.created_at), "dd MMMM yyyy, HH:mm", { locale: hy })}</p>
                  </div>
                </div>
              </div>

              {selectedOrder.project_description && (
                <div>
                  <h3 className="font-semibold mb-2">Նախագծի նկարագրություն</h3>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {selectedOrder.project_description}
                  </p>
                </div>
              )}

              {selectedOrder.brief_answers && Object.keys(selectedOrder.brief_answers).length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Բրիֆ պատասխաններ</h3>
                  <div className="space-y-2">
                    {Object.entries(selectedOrder.brief_answers).map(([key, value]) => (
                      <div key={key} className="text-sm">
                        <span className="text-muted-foreground">{key}:</span>{" "}
                        {Array.isArray(value) ? value.join(", ") : String(value)}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
